import { useState, useEffect } from 'react';
import { createClient } from '@blinkdotnew/sdk';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Activity, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Zap,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  Filter,
  Calendar
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { safeJsonParse } from '@/lib/utils';

interface TelemetryReport {
  id: string;
  userId: string | null;
  reportType: string;
  metrics: string;
  context: string;
  timestamp: string;
  createdAt: string;
}

interface ParsedMetrics {
  marks: Array<{
    name: string;
    duration?: number;
    metadata?: Record<string, any>;
  }>;
  aggregates: {
    totalLoadTime: number;
    slowestOperation: { name: string; duration: number } | null;
    fastestOperation: { name: string; duration: number } | null;
    averageLoadTime: number;
  };
  sessionId: string;
}

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

export default function TelemetryDashboard() {
  const [user, setUser] = useState<any>(null);
  const [reports, setReports] = useState<TelemetryReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d' | 'all'>('24h');

  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      if (state.user && !state.isLoading) {
        setUser(state.user);
        await loadReports(state.user.id);
      }
    });
    return unsubscribe;
  }, [timeRange]);

  const loadReports = async (userId: string) => {
    setLoading(true);
    try {
      // Calculate time filter based on selected range
      let sinceDate: Date | null = null;
      const now = new Date();
      
      if (timeRange === '1h') {
        sinceDate = new Date(now.getTime() - 60 * 60 * 1000);
      } else if (timeRange === '24h') {
        sinceDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      } else if (timeRange === '7d') {
        sinceDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      }

      const allReports = await blink.db.telemetryReports.list({
        where: { userId },
        orderBy: { timestamp: 'desc' },
        limit: 100,
      });

      // Filter by time range
      const filtered = sinceDate 
        ? allReports.filter(r => new Date(r.timestamp) >= sinceDate!)
        : allReports;

      setReports(filtered);
    } catch (error) {
      console.error('Error loading telemetry reports:', error);
      toast({
        title: 'Error',
        description: 'Failed to load telemetry reports',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const parseMetrics = (report: TelemetryReport): ParsedMetrics | null => {
    try {
      return safeJsonParse(report.metrics);
    } catch {
      return null;
    }
  };

  const calculateStats = () => {
    const metrics = reports.map(r => parseMetrics(r)).filter(Boolean) as ParsedMetrics[];
    
    if (metrics.length === 0) {
      return {
        avgLoadTime: 0,
        slowestLoad: 0,
        fastestLoad: 0,
        totalReports: 0,
        criticalIssues: 0,
      };
    }

    const loadTimes = metrics.map(m => m.aggregates.averageLoadTime);
    const avgLoadTime = loadTimes.reduce((a, b) => a + b, 0) / loadTimes.length;
    const slowestLoad = Math.max(...loadTimes);
    const fastestLoad = Math.min(...loadTimes);
    const criticalIssues = metrics.filter(m => 
      m.aggregates.slowestOperation && m.aggregates.slowestOperation.duration > 5000
    ).length;

    return {
      avgLoadTime,
      slowestLoad,
      fastestLoad,
      totalReports: reports.length,
      criticalIssues,
    };
  };

  const stats = calculateStats();

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Please sign in to view telemetry data</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Performance Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Monitor application performance and telemetry data
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={(v: any) => setTimeRange(v)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Last Hour</SelectItem>
              <SelectItem value="24h">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={() => user && loadReports(user.id)}
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{stats.totalReports}</span>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Avg Load Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{Math.round(stats.avgLoadTime)}ms</span>
              <Clock className="h-4 w-4 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Fastest Load
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-green-600">{Math.round(stats.fastestLoad)}ms</span>
              <Zap className="h-4 w-4 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Slowest Load
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-orange-600">{Math.round(stats.slowestLoad)}ms</span>
              <TrendingUp className="h-4 w-4 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Critical Issues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl font-bold ${stats.criticalIssues > 0 ? 'text-red-600' : ''}`}>
                {stats.criticalIssues}
              </span>
              <AlertTriangle className={`h-4 w-4 ${stats.criticalIssues > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visual Charts */}
      <Tabs defaultValue="timeline" className="space-y-4">
        <TabsList>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="operations">Operations</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance Over Time</CardTitle>
              <CardDescription>
                Load time trends in the selected time range
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={reports.slice(0, 20).reverse().map((report, idx) => {
                    const metrics = parseMetrics(report);
                    return {
                      index: idx + 1,
                      avgLoadTime: metrics ? Math.round(metrics.aggregates.averageLoadTime) : 0,
                      slowestOp: metrics?.aggregates.slowestOperation ? Math.round(metrics.aggregates.slowestOperation.duration) : 0,
                    };
                  })}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="index" label={{ value: 'Report #', position: 'insideBottom', offset: -5 }} />
                  <YAxis label={{ value: 'Time (ms)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="avgLoadTime" stroke="#3b82f6" name="Avg Load Time" />
                  <Line type="monotone" dataKey="slowestOp" stroke="#f59e0b" name="Slowest Operation" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="operations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Top Slow Operations</CardTitle>
              <CardDescription>
                Operations with the highest average duration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(() => {
                    const operationStats = new Map<string, { total: number; count: number }>();
                    
                    reports.slice(0, 20).forEach(report => {
                      const metrics = parseMetrics(report);
                      if (metrics?.marks) {
                        metrics.marks.forEach(mark => {
                          if (mark.duration) {
                            const existing = operationStats.get(mark.name) || { total: 0, count: 0 };
                            existing.total += mark.duration;
                            existing.count += 1;
                            operationStats.set(mark.name, existing);
                          }
                        });
                      }
                    });

                    return Array.from(operationStats.entries())
                      .map(([name, stats]) => ({
                        name: name.length > 20 ? name.substring(0, 17) + '...' : name,
                        avgDuration: Math.round(stats.total / stats.count),
                      }))
                      .sort((a, b) => b.avgDuration - a.avgDuration)
                      .slice(0, 10);
                  })()}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis label={{ value: 'Avg Duration (ms)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Bar dataKey="avgDuration" fill="#f59e0b" name="Avg Duration" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance Reports</CardTitle>
              <CardDescription>
                Detailed telemetry data from your application
              </CardDescription>
            </CardHeader>
            <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No telemetry data available for the selected time range</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-4">
                {reports.map((report) => {
                  const metrics = parseMetrics(report);
                  if (!metrics) return null;

                  const isCritical = metrics.aggregates.slowestOperation && 
                    metrics.aggregates.slowestOperation.duration > 5000;

                  return (
                    <Card key={report.id} className={isCritical ? 'border-red-500' : ''}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Activity className="h-4 w-4" />
                            <CardTitle className="text-sm">
                              Session {metrics.sessionId.substring(0, 8)}
                            </CardTitle>
                          </div>
                          <div className="flex items-center gap-2">
                            {isCritical && (
                              <Badge variant="destructive" className="text-xs">
                                Critical
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {new Date(report.timestamp).toLocaleString()}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Avg Load Time</p>
                            <p className="font-semibold">{Math.round(metrics.aggregates.averageLoadTime)}ms</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Total Time</p>
                            <p className="font-semibold">{Math.round(metrics.aggregates.totalLoadTime)}ms</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Operations</p>
                            <p className="font-semibold">{metrics.marks.length}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Slowest Op</p>
                            <p className="font-semibold">
                              {metrics.aggregates.slowestOperation 
                                ? `${Math.round(metrics.aggregates.slowestOperation.duration)}ms`
                                : 'N/A'}
                            </p>
                          </div>
                        </div>

                        {metrics.aggregates.slowestOperation && (
                          <div className="pt-3 border-t">
                            <p className="text-xs text-muted-foreground mb-2">Slowest Operation:</p>
                            <div className="flex items-center justify-between bg-muted/30 p-2 rounded">
                              <span className="text-sm font-medium">
                                {metrics.aggregates.slowestOperation.name}
                              </span>
                              <Badge variant={isCritical ? 'destructive' : 'secondary'}>
                                {Math.round(metrics.aggregates.slowestOperation.duration)}ms
                              </Badge>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
